import { Checkmark, CopyFile } from '@carbon/icons-react';
import { Accordion, AccordionItem, Button, Tooltip } from '@carbon/react';
import find from 'lodash/find';
import get from 'lodash/get';
import toNumber from 'lodash/toNumber';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

import BrokenImg from 'assets/broken_img.svg';
import DefaultModelAvatar from 'assets/v3/app_logo.png';
import { ReactComponent as ViewModeIcon } from 'assets/view_mode_icon.svg';
import { CodeBlock } from 'components_v3/markdown/code-block';
import { MemoizedReactMarkdown } from 'components_v3/markdown/memoized-react-markdown';
import { useImagePreview } from 'context/image-preview';
import { useCopyToClipboard } from 'hooks/conversations';
import { prepareLatexContent } from 'utils/latexPreprocessor';
import { displayMessageTime } from 'utils/message';
import { getAbsoluteURL, prettifyResponseMessage } from 'utils/utils';

import PopoverWrapper from './PopoverWrapper';
import VideoPlayer from './VideoPlayer';

import './ConversationMessageAssistant.scss';

const ConversationMessageAssistant = ({
  message,
  snapshot_llms = [],
  markdownView,
  latexView,
  onBetterFormatClick,
}) => {
  const { t, i18n } = useTranslation();
  const { isCopied, handleCopyToClipboard } = useCopyToClipboard();
  const { openImagePreview } = useImagePreview();

  const allModels = snapshot_llms;
  const responseMessage = prettifyResponseMessage(message.content);

  const assistantModel = useMemo(
    () => find(allModels, (model) => model.id === message.model),
    [message, allModels],
  );

  return (
    <>
      <header className='message-header'>
        <div className='header-content'>
          <PopoverWrapper
            className='avatar-wrapper'
            content={
              <div className='avatar-popover'>
                {assistantModel?.display_name || t('SkyDeck.ai')}
              </div>
            }>
            <img
              className='avatar'
              src={assistantModel?.icon || DefaultModelAvatar}
              alt={t('Bot')}
            />
          </PopoverWrapper>
          <div className='message-info'>
            <span className='message-info__sender-name'>
              {assistantModel?.display_name || t('SkyDeck.ai')}
            </span>
            {message.timestamp && (
              <span className='message-info__time'>
                {displayMessageTime(message.timestamp, i18n.language)}
                {message.edited_at && <span> </span>}
                {message.edited_at && (
                  <Tooltip
                    className='edited-tooltip'
                    align='bottom'
                    label={t('{{time}}', {
                      time:
                        displayMessageTime(message.edited_at, i18n.language)
                          .charAt(0)
                          .toUpperCase() +
                        displayMessageTime(message.edited_at, i18n.language).slice(1),
                    })}>
                    <span className='edited-indicator'>{'(edited)'}</span>
                  </Tooltip>
                )}
              </span>
            )}
          </div>
        </div>
        <div className='actions'>
          <Button
            className='better-format-link view-mode-button'
            onClick={onBetterFormatClick}
            hasIconOnly
            renderIcon={ViewModeIcon}
            iconDescription={t('Change view')}
            tooltipPosition='bottom'
            kind='ghost'
            as='block'
          />
          <Button
            as='block'
            onClick={() => handleCopyToClipboard(responseMessage)}
            className='copy-button'
            hasIconOnly
            renderIcon={isCopied ? Checkmark : CopyFile}
            iconDescription={t('Copy text')}
            tooltipPosition='bottom'
            kind='ghost'
          />
        </div>
      </header>
      <div className='content'>
        <div className='conversation-message-assistant-container'>
          <div className='text'>
            {markdownView ? (
              <MemoizedReactMarkdown
                key={latexView}
                remarkPlugins={[remarkGfm, ...(latexView ? [remarkMath, remarkBreaks] : [])]}
                escapeHtml={false}
                rehypePlugins={[rehypeRaw, ...(latexView ? [rehypeKatex] : [])]}
                linkTarget='_blank'
                components={{
                  img: ({ node }) => {
                    let prop = node.properties;

                    // Handle image-id attribute for AI-generated images
                    if (prop['image-id']) {
                      const image = find(get(message, ['upload_files', 'images']), {
                        id: toNumber(prop['image-id']),
                      });

                      if (image) {
                        prop.src = image.url;
                        prop.alt = image.file_name;
                      }
                    }
                    const finalSrc = getAbsoluteURL(prop.src);

                    return (
                      <img
                        className='generated-image'
                        src={finalSrc}
                        alt={prop.alt}
                        style={{ cursor: 'pointer' }}
                        onClick={() => openImagePreview(finalSrc, prop.alt)}
                        onError={(event) => {
                          event.target.src = BrokenImg;
                        }}
                      />
                    );
                  },
                  code({ inline, className, children, ...props }) {
                    if (children && children.length) {
                      if (children[0] == '▍') {
                        return <span>▍</span>;
                      }

                      children[0] = children[0].replace('▍', '▍');
                    }

                    const match = /language-(\w+)/.exec(className || '');

                    return !inline ? (
                      <CodeBlock
                        key={Math.random()}
                        language={(match && match[1]) || ''}
                        value={String(children).replace(/\n$/, '')}
                        {...props}
                      />
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  },
                  details: ({ children }) => {
                    const [isOpen, setIsOpen] = useState(false);

                    const summaryElement = children?.find((child) => child?.type === 'summary');
                    const summaryText = summaryElement?.props?.children || t('Summary');

                    return (
                      <Accordion>
                        <AccordionItem
                          open={isOpen}
                          title={summaryText}
                          onHeadingClick={({ isOpen }) => {
                            setIsOpen(isOpen);
                          }}>
                          {children?.filter((child) => child?.type !== 'summary')}
                        </AccordionItem>
                      </Accordion>
                    );
                  },
                  think: ({ node }) => {
                    let thinkingContent = responseMessage.slice(
                      get(node, ['position', 'start', 'offset']),
                      get(node, ['position', 'end', 'offset']),
                    );

                    thinkingContent = thinkingContent.replace(/<\/?think>/g, '');

                    return (
                      <Accordion>
                        <AccordionItem title={t('Thinking process')}>
                          <CodeBlock language='' value={thinkingContent} />
                        </AccordionItem>
                      </Accordion>
                    );
                  },
                }}>
                {latexView ? prepareLatexContent(responseMessage) : responseMessage}
              </MemoizedReactMarkdown>
            ) : (
              <p style={{ whiteSpace: 'pre-wrap' }}>
                {responseMessage}
              </p>
            )}
          </div>

          {((message.upload_files?.videos && message.upload_files.videos.length > 0) ||
            (message.message_videos && message.message_videos.length > 0)) && (
            <VideoPlayer videos={message.upload_files?.videos || message.message_videos} />
          )}
        </div>
      </div>
    </>
  );
};

ConversationMessageAssistant.propTypes = {
  message: PropTypes.object.isRequired,
  snapshot_llms: PropTypes.array,
  markdownView: PropTypes.bool.isRequired,
  latexView: PropTypes.bool.isRequired,
  onBetterFormatClick: PropTypes.func.isRequired,
};

export default ConversationMessageAssistant;

export const ConversationMessageAssistantPlaceholder = ({ assistantModel }) => {
  const { t } = useTranslation();

  return (
    <li className='message assistant-message'>
      <div className='message-wrapper'>
        <header className='message-header'>
          <div className='header-content'>
            <PopoverWrapper
              className='avatar-wrapper'
              content={
                <div className='avatar-popover'>
                  {assistantModel?.display_name || t('SkyDeck.ai')}
                </div>
              }>
              <img
                className='avatar'
                src={assistantModel?.icon || DefaultModelAvatar}
                alt={t('Bot')}
              />
            </PopoverWrapper>
            <div className='message-info'>
              <span className='message-info__sender-name'>
                {assistantModel?.display_name || t('SkyDeck.ai')}
              </span>
              <span className='message-info__time'>{t('Processing...')}</span>
            </div>
          </div>
        </header>
        <div className='content'>
          <div className='conversation-message-assistant-container'>
          </div>
        </div>
      </div>
    </li>
  );
};

ConversationMessageAssistantPlaceholder.propTypes = {
  assistantModel: PropTypes.object,
};
